import React, { useState, useEffect } from 'react';
import {
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Typography,
    Alert,
    FormHelperText,
    Chip,
    Paper,
} from '@mui/material';
import {
    OpenInNew,
    PhoneIphone,
    Link as LinkIcon,
    Cancel,
} from '@mui/icons-material';
import { useTranslate, FormDataConsumer } from 'react-admin';

interface ActionTypeSelectorProps {
    actionType?: string;
    actionTarget?: string;
    onActionTypeChange: (value: string) => void;
    onActionTargetChange: (value: string) => void;
}

const APP_SCREENS = [
    { id: 'Home', requiresParams: false },
    { id: 'Chargers', requiresParams: false },
    { id: 'ChargerDetail', requiresParams: true },
    { id: 'Sites', requiresParams: false },
    { id: 'SiteDetail', requiresParams: true },
    { id: 'Profile', requiresParams: false },
    { id: 'PersonalInformation', requiresParams: false },
    { id: 'MyVehicles', requiresParams: false },
    { id: 'PaymentMethods', requiresParams: false },
    { id: 'ChargingHistory', requiresParams: false },
    { id: 'ChargingSession', requiresParams: true },
    { id: 'Notifications', requiresParams: false },
    { id: 'NotificationDetail', requiresParams: false },
    { id: 'Support', requiresParams: false },
    { id: 'FAQ', requiresParams: false },
    { id: 'About', requiresParams: false },
];

const ActionTypeSelector: React.FC<ActionTypeSelectorProps> = ({
    actionType = 'none',
    actionTarget = '',
    onActionTypeChange,
    onActionTargetChange,
}) => {
    const translate = useTranslate();
    const [selectedScreen, setSelectedScreen] = useState('');
    const [screenParams, setScreenParams] = useState('');
    const [urlValue, setUrlValue] = useState('');

    // Parse existing action target when component mounts or actionTarget changes
    useEffect(() => {
        if (actionType === 'screen' && actionTarget) {
            try {
                const targetData = JSON.parse(actionTarget);
                setSelectedScreen(targetData.screen || '');
                setScreenParams(JSON.stringify(targetData.params || {}));
            } catch {
                // If not JSON, treat as screen name only
                setSelectedScreen(actionTarget);
                setScreenParams('{}');
            }
        } else if ((actionType === 'url' || actionType === 'deeplink') && actionTarget) {
            setUrlValue(actionTarget);
        }
    }, [actionType, actionTarget]);

    const handleActionTypeChange = (value: string) => {
        onActionTypeChange(value);
        // Clear action target when type changes
        onActionTargetChange('');
        setSelectedScreen('');
        setScreenParams('{}');
        setUrlValue('');
    };

    const handleScreenChange = (screen: string) => {
        setSelectedScreen(screen);
        const screenConfig = APP_SCREENS.find(s => s.id === screen);

        // Create the action target based on screen selection
        let target = '';
        if (screen === 'NotificationDetail') {
            // Special handling for NotificationDetail - will use the message ID
            target = JSON.stringify({
                screen: 'NotificationDetail',
                params: { notificationId: '{{messageId}}' }
            });
        } else if (screenConfig?.requiresParams) {
            target = JSON.stringify({
                screen,
                params: {}
            });
        } else {
            target = screen;
        }

        onActionTargetChange(target);
    };

    const handleScreenParamsChange = (params: string) => {
        setScreenParams(params);
        try {
            const parsedParams = JSON.parse(params);
            const target = JSON.stringify({
                screen: selectedScreen,
                params: parsedParams
            });
            onActionTargetChange(target);
        } catch {
            // Invalid JSON, don't update
        }
    };

    const handleUrlChange = (url: string) => {
        setUrlValue(url);
        onActionTargetChange(url);
    };

    const getActionIcon = (type: string) => {
        switch (type) {
            case 'url': return <OpenInNew />;
            case 'screen': return <PhoneIphone />;
            case 'deeplink': return <LinkIcon />;
            default: return <Cancel />;
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
                <InputLabel>{translate('resources.messages.fields.actionType')}</InputLabel>
                <Select
                    value={actionType}
                    onChange={(e) => handleActionTypeChange(e.target.value)}
                    label={translate('resources.messages.fields.actionType')}
                >
                    <MenuItem value="none">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Cancel fontSize="small" />
                            <span>{translate('resources.messages.actionTypes.none')}</span>
                        </Box>
                    </MenuItem>
                    <MenuItem value="url">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <OpenInNew fontSize="small" />
                            <span>{translate('resources.messages.actionTypes.url')}</span>
                        </Box>
                    </MenuItem>
                    <MenuItem value="screen">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PhoneIphone fontSize="small" />
                            <span>{translate('resources.messages.actionTypes.screen')}</span>
                        </Box>
                    </MenuItem>
                    <MenuItem value="deeplink">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinkIcon fontSize="small" />
                            <span>{translate('resources.messages.actionTypes.deeplink')}</span>
                        </Box>
                    </MenuItem>
                </Select>
                <FormHelperText>
                    {actionType === 'none' && translate('resources.messages.actionHelp.none')}
                    {actionType === 'url' && translate('resources.messages.actionHelp.url')}
                    {actionType === 'screen' && translate('resources.messages.actionHelp.screen')}
                    {actionType === 'deeplink' && translate('resources.messages.actionHelp.deeplink')}
                </FormHelperText>
            </FormControl>

            {actionType === 'screen' && (
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                        {translate('resources.messages.selectScreen')}
                    </Typography>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>{translate('resources.messages.appScreen')}</InputLabel>
                        <Select
                            value={selectedScreen}
                            onChange={(e) => handleScreenChange(e.target.value)}
                            label={translate('resources.messages.appScreen')}
                        >
                            {APP_SCREENS.map(screen => (
                                <MenuItem key={screen.id} value={screen.id}>
                                    <Box>
                                        <Typography variant="body1">
                                            {translate(`resources.messages.appScreens.${screen.id}.name`)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {translate(`resources.messages.appScreens.${screen.id}.description`)}
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {selectedScreen === 'NotificationDetail' && (
                        <Alert severity="info" sx={{ mt: 1 }}>
                            {translate('resources.messages.notificationDetailInfo')}
                        </Alert>
                    )}

                    {selectedScreen && APP_SCREENS.find(s => s.id === selectedScreen)?.requiresParams && selectedScreen !== 'NotificationDetail' && (
                        <TextField
                            fullWidth
                            label={translate('resources.messages.screenParams')}
                            value={screenParams}
                            onChange={(e) => handleScreenParamsChange(e.target.value)}
                            helperText={translate('resources.messages.screenParamsHelp')}
                            placeholder='{"id": "123"}'
                            multiline
                            rows={2}
                        />
                    )}
                </Paper>
            )}

            {(actionType === 'url' || actionType === 'deeplink') && (
                <TextField
                    fullWidth
                    label={
                        actionType === 'url'
                            ? translate('resources.messages.fields.websiteUrl')
                            : translate('resources.messages.fields.deeplinkUrl')
                    }
                    value={urlValue}
                    onChange={(e) => handleUrlChange(e.target.value)}
                    placeholder={
                        actionType === 'url'
                            ? 'https://example.com/promotion'
                            : 'myapp://promotion/special-offer'
                    }
                    helperText={
                        actionType === 'url'
                            ? translate('resources.messages.urlHelp')
                            : translate('resources.messages.deeplinkHelp')
                    }
                />
            )}

            {actionType !== 'none' && actionTarget && (
                <Paper sx={{ p: 1.5, bgcolor: 'success.light', mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                        {translate('resources.messages.actionTargetValue')}:
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                        {actionTarget}
                    </Typography>
                </Paper>
            )}
        </Box>
    );
};

export default ActionTypeSelector;